import * as R from 'ramda';
import { Endpoints } from '../../../core/api/endpoints';
import { ApiReducer, makeApiReducer, makeApiAction, makeRsaaTypes } from '../../../core/api/rsaa';
import { addError, noLoadingNoError, addLoading, getLoading } from '../../../core/api/module';
import { ActionType } from '../../../core/redux/actionType';


const KEY_ACTION = ActionType.UpdateDevPlanItemTitleRequest;
const ACTIONS    = [
  ActionType.UpdateDevPlanItemTitleRequest,
  ActionType.UpdateDevPlanItemTitleResponse,
  ActionType.UpdateDevPlanItemTitleError,
];


type Payload = undefined;
type Meta = {
  itemId: number;
  title: string;
  competencyId: number;
  competencyName: string;
};


/**
 * Dispatch this action to update the title for a dev plan item
 */
export const updateDevPlanItemTitleAction = (
  participantId: number,
  projectId: number,
  itemId: number,
  title: string,
  competencyId: number,
  competencyName: string,
) => makeApiAction<Payload, Meta>({
  method:   'POST',
  endpoint: Endpoints.UpdateDevPlanItemTitle,
  headers:  { 'content-type': 'application/json' },
  body:     state => JSON.stringify({
    participantId: participantId,
    projectId:     projectId,
    devItemId:     itemId,
    title:         title,
    competencyId:  competencyId,
    languageId:    state.locale.lang.languageId,
  }),
  types:    makeRsaaTypes<Payload, Meta>(ACTIONS, { itemId, title, competencyId, competencyName }),
  bailout:  state => !!getLoading(state, KEY_ACTION, itemId),
});


const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION, action.meta.itemId);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, action.meta.itemId);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, action.meta.itemId);

  const itemIndex = successState.devPlan?.items?.findIndex(item => item.devItemId === action.meta.itemId) ?? -1;

  // Can't update an item we didn't find ¯\_(ツ)_/¯
  if (itemIndex === -1) {
    return successState;
  }

  const newTitle = action.meta.competencyId > 0 ? action.meta.competencyName : action.meta.title;

  const item = R.assoc('title', newTitle, successState.devPlan!.items![itemIndex]);
  const updatedItem = R.assoc('devItemCompetencyId', action.meta.competencyId, item);
  
  return R.assocPath(
    ['devPlan', 'items'],
    R.update(itemIndex, updatedItem, successState.devPlan!.items!),
    successState,
  );
};


export const updateDevPlanItemTitleReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
